import React, { useState, useEffect } from 'react';
import { Standard } from '../../../data/types';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';
import { motion } from 'framer-motion';

interface LearningPhaseGameProps {
  standard: Standard | null;
  onComplete: (score: number) => void;
}

const LearningPhaseGame: React.FC<LearningPhaseGameProps> = ({ standard, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [currentScore, setCurrentScore] = useState(0);
  const [gameTime, setGameTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  // Simulate game loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Game timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (gameStarted && !gameCompleted && !isPaused) {
      interval = setInterval(() => {
        setGameTime(prev => prev + 1);
        
        // Simulate automatic score increase during gameplay
        if (Math.random() > 0.7) { // 30% chance each second
          setCurrentScore(prev => prev + 10);
        }
        
        // Auto-complete game after 20 seconds for demo
        if (gameTime >= 20) {
          handleCompleteGame();
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameStarted, gameCompleted, isPaused, gameTime]);
  
  const handleStartGame = () => {
    setGameStarted(true);
  };
  
  const handleTogglePause = () => {
    setIsPaused(prev => !prev);
  };
  
  const handleCompleteGame = () => {
    setGameCompleted(true);
    // Add some final points for completing
    const finalScore = currentScore + 25;
    setCurrentScore(finalScore);
    
    // Notify parent after a short delay
    setTimeout(() => {
      onComplete(finalScore);
    }, 2000);
  };
  
  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-b from-green-50 to-teal-50 p-6 rounded-xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-green-600 flex items-center">
          <GameIcons.Gamepad className="mr-2" size={24} color="#047857" />
          <span>Game Challenge</span>
        </h2>
        
        {gameStarted && !gameCompleted && (
          <div className="flex items-center space-x-4">
            <div className="flex items-center px-3 py-1 bg-teal-100 text-teal-800 rounded-full">
              <GameIcons.Clock size={16} className="mr-1" color="currentColor" />
              <span>{formatTime(gameTime)}</span>
            </div>
            
            <div className="flex items-center px-3 py-1 bg-amber-100 text-amber-800 rounded-full">
              <GameIcons.Trophy size={16} className="mr-1" color="currentColor" />
              <span>{currentScore} pts</span>
            </div>
          </div>
        )}
      </div>
      
      {loading ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center h-96 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-l-4 border-teal-500 mb-6"></div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Game...</h3>
          <p className="text-gray-600">Preparing your math adventure</p>
        </div>
      ) : !gameStarted ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center relative overflow-hidden">
          {/* Decorative game elements */}
          <div className="absolute top-5 left-5 w-10 h-10 rounded-full bg-teal-100 opacity-50"></div>
          <div className="absolute bottom-10 right-10 w-16 h-16 rounded-full bg-green-100 opacity-50"></div>
          <div className="absolute top-20 right-20 w-8 h-8 rounded-full bg-amber-100 opacity-50"></div>
          
          <div className="relative z-10 mb-6">
            <GameIcons.Gamepad size={64} className="mx-auto mb-4" color="#047857" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Ready to Play?
            </h3>
            <p className="text-gray-600 max-w-lg mx-auto mb-8">
              It's time to practice your {standard?.title || 'math'} skills with a fun interactive game! 
              Complete challenges to earn points and master this concept.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mr-3">
                  <GameIcons.Info size={20} color="#047857" />
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-gray-800 mb-1">Game Details</h4>
                  <p className="text-gray-600 mb-2">Standard: {standard?.code || 'Math Standard'}</p>
                  <p className="text-gray-600 mb-0">Challenge Level: {standard?.gradeLevel || 'Grade'} Math</p>
                </div>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStartGame}
              className="px-8 py-4 bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white rounded-lg shadow-lg font-bold text-lg flex items-center justify-center mx-auto"
            >
              <GameIcons.Play size={20} className="mr-2" color="#FFFFFF" />
              Start Game
            </motion.button>
          </div>
        </div>
      ) : !gameCompleted ? (
        <div className="relative">
          {/* Game interface mockup */}
          <div className="bg-white rounded-xl shadow-lg p-4 aspect-video relative overflow-hidden">
            {/* Game visualization */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-900 to-green-800 flex items-center justify-center">
              <div className="absolute top-10 left-10 w-16 h-16 rounded-full bg-green-400 opacity-20 animate-pulse"></div>
              <div className="absolute bottom-20 right-20 w-24 h-24 rounded-full bg-teal-400 opacity-20 animate-pulse delay-300"></div>
              <div className="absolute top-40 right-40 w-12 h-12 rounded-full bg-yellow-400 opacity-20 animate-pulse delay-700"></div>
              
              {isPaused ? (
                <div className="text-center text-white">
                  <GameIcons.Pause size={80} className="mx-auto mb-4 opacity-70" color="#FFFFFF" />
                  <h3 className="text-3xl font-bold mb-4">Game Paused</h3>
                  <p className="text-lg mb-6">Take a quick break!</p>
                  <button
                    onClick={handleTogglePause}
                    className="px-6 py-3 bg-white text-teal-800 rounded-lg shadow-lg hover:bg-gray-100 transition-colors font-bold"
                  >
                    Resume Game
                  </button>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white">
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold mb-2">Math Challenge Game</h3>
                    <p className="text-lg opacity-80">Solve puzzles to earn points!</p>
                  </div>
                  
                  {/* Simulated game elements */}
                  <div className="flex flex-wrap justify-center gap-4 max-w-2xl">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ 
                          opacity: 1, 
                          scale: 1,
                          y: [0, -10, 0],
                        }}
                        transition={{ 
                          delay: index * 0.2,
                          y: { 
                            repeat: Infinity, 
                            duration: 2,
                            repeatType: 'reverse',
                            delay: index * 0.3
                          }
                        }}
                        className="w-24 h-24 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-opacity-30 transition-colors shadow-lg"
                      >
                        <span className="text-2xl font-bold">
                          {Math.floor(Math.random() * 10) + 1}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="mt-8 text-center">
                    <p className="text-lg mb-2">Connect the numbers to reach the target!</p>
                    <div className="inline-block px-6 py-2 bg-white bg-opacity-20 rounded-full backdrop-blur-sm">
                      <span className="text-2xl font-bold">Target: 15</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Game controls overlay */}
            <div className="absolute top-4 right-4 flex space-x-2">
              <button 
                onClick={handleTogglePause}
                className="w-10 h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-colors shadow-md"
              >
                {isPaused ? 
                  <GameIcons.Play size={20} color="#047857" /> : 
                  <GameIcons.Pause size={20} color="#047857" />
                }
              </button>
              
              <button 
                onClick={handleCompleteGame}
                className="w-10 h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-colors shadow-md"
              >
                <GameIcons.Flag size={20} color="#047857" />
              </button>
            </div>
          </div>
          
          {/* Game controls and info */}
          <div className="mt-4 flex justify-between">
            <div className="flex space-x-2">
              <button
                onClick={handleTogglePause}
                className="px-4 py-2 bg-white text-teal-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center shadow"
              >
                {isPaused ? 
                  <>
                    <GameIcons.Play size={18} className="mr-1" color="currentColor" />
                    <span>Resume</span>
                  </> : 
                  <>
                    <GameIcons.Pause size={18} className="mr-1" color="currentColor" />
                    <span>Pause</span>
                  </>
                }
              </button>
              
              <button
                className="px-4 py-2 bg-white text-teal-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center shadow"
              >
                <GameIcons.VolumeUp size={18} className="mr-1" color="currentColor" />
                <span>Sound</span>
              </button>
            </div>
            
            <button
              onClick={handleCompleteGame}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center shadow"
            >
              <GameIcons.Flag size={18} className="mr-1" color="currentColor" />
              <span>Finish Game</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 15 }}
            className="w-24 h-24 rounded-full bg-gradient-to-r from-teal-500 to-green-500 flex items-center justify-center mx-auto mb-4"
          >
            <GameIcons.Trophy size={48} color="#FFFFFF" />
          </motion.div>
          
          <motion.h3 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold text-gray-800 mb-2"
          >
            Game Complete!
          </motion.h3>
          
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-gray-600 mb-6"
          >
            Great job! You've completed the game challenge for this standard.
          </motion.p>
          
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-teal-50 rounded-lg p-6 mb-6 max-w-md mx-auto"
          >
            <h4 className="font-bold text-teal-800 mb-3">Your Results</h4>
            <div className="flex justify-center items-center space-x-8">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Score</p>
                <p className="text-3xl font-bold text-teal-700">{currentScore}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Time</p>
                <p className="text-3xl font-bold text-teal-700">{formatTime(gameTime)}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-1">Standard</p>
                <p className="text-xl font-bold text-teal-700">{standard?.code || 'Math'}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <button
              onClick={() => onComplete(currentScore)}
              className="px-6 py-3 bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white rounded-lg shadow-md font-bold flex items-center justify-center mx-auto"
            >
              <span>Continue</span>
              <GameIcons.ArrowRight size={18} className="ml-2" color="currentColor" />
            </button>
          </motion.div>
        </div>
      )}
      
      {/* Auto-save indicator */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center text-xs text-green-600">
          <GameIcons.CloudCheck size={14} className="mr-1" color="currentColor" />
          Your progress is being saved automatically
        </span>
      </div>
    </div>
  );
};

export default LearningPhaseGame;
